import { EEthereumNetworkId } from '@ankr.com/provider';
import { NetworkConfig } from 'config';
import { AbiItem } from 'web3-utils';

import { AVAILABLE_CHAINS } from 'modules/common';

import CRON_TASK_FACTORY_ABI from './contracts/cronTaskFactory.json';
import REGISTRAR_ABI from './contracts/registrar.json';
import REGISTRY_ABI from './contracts/registry.json';
import { CronTaskFactory, Registrar, Registry } from './typechain';
import { BaseContract } from './typechain/types';
import { Web3Provider } from './types';
import { getNetworkConfig, isWeb3KeyWriteProvider } from './utils';
import { UtilsManager } from './UtilsManager';

export class BaseContractManager extends UtilsManager {
  public get currentAccount(): string | null {
    if (isWeb3KeyWriteProvider(this.provider)) {
      return this.provider.currentAccount;
    }

    return null;
  }

  public async getProvider(): Promise<Web3Provider> {
    const chainId = await this.getChainId();
    const isCorrectChain = AVAILABLE_CHAINS.includes(chainId);
    const isWriteProvider = isWeb3KeyWriteProvider(this.provider);

    if (isWriteProvider && isCorrectChain && !this.provider.isConnected()) {
      await this.provider.connect();
    }

    return this.provider;
  }

  public getChainId(): Promise<EEthereumNetworkId> {
    const web3 = this.provider.getWeb3();

    return web3.eth.getChainId();
  }

  public getCustomContract(
    address: string,
    abi: AbiItem[],
  ): Promise<BaseContract> {
    return this.getContract(address, abi);
  }

  public async getCronTaskFactoryContract(): Promise<CronTaskFactory> {
    const { contracts } = await this.getNetworkConfig();

    return this.getContract(contracts.cronTaskFactory, CRON_TASK_FACTORY_ABI);
  }

  public async getRegistryContract(): Promise<Registry> {
    const { contracts } = await this.getNetworkConfig();

    return this.getContract(contracts.registry, REGISTRY_ABI);
  }

  public async getRegistrarContract(): Promise<Registrar> {
    const { contracts } = await this.getNetworkConfig();

    return this.getContract(contracts.registrar, REGISTRAR_ABI);
  }

  private async getContract<T extends BaseContract>(
    address: string,
    abi: unknown,
  ): Promise<T> {
    const web3 = (await this.getProvider()).getWeb3();

    return new web3.eth.Contract(abi as AbiItem[], address) as unknown as T;
  }

  private async getNetworkConfig(): Promise<NetworkConfig> {
    const chainId = await this.getChainId();
    if (!chainId) {
      throw new Error(`Chain id is not defined`);
    }

    return getNetworkConfig(chainId);
  }
}
